import { Component, EventEmitter, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { ZkIOption } from '@zenklub/ng-ui/component/input/dropdown/dropdown.interface';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ApiError, ApiErrorResponse } from 'app/interfaces/error.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { UserService } from 'app/services/user.service';
import { ZkuService } from 'app/services/zku.service';
import { timezones } from 'shared/constants/timezones.const';

import { IProfessionalProfile } from '../../interfaces/professionalprofile.interface';
import { Tier, allTiers } from './helpers/tiers';
import { ModalSteps, UpdateSessionPrice } from './plan-and-session.interface';

@Component({
	selector: 'app-my-account-plan-and-session',
	templateUrl: './plan-and-session.component.html',
	styleUrls: ['./plan-and-session.component.scss'],
})
export class MyAccountPlanAndSessionComponent {
	@Output() unsavedChanges = new EventEmitter<boolean>();
	@Output() userUpdated = new EventEmitter();

	isLoading = true;
	isSaving = false;
	isSavingModal = false;
	isPremium = false;
	canUpdateOneHour = false;
	canUpdateHalfHour = false;
	contactTiers: string[] = [];

	marginOptions: ZkIOption[] = [];
	timezoneOptions: ZkIOption[] = [];
	tagsIds: string[] = [];
	tiers: Tier[] = allTiers;
	selectedTierThatAcceptsHalfHour = false;

	formChanged = false;
	isOpenModal = false;
	modalStep: ModalSteps;
	modalError = '';
	isProfessionalZen = false;
	modalData = {
		priceHalfHour: 0,
		priceOneHour: 0,
	};

	data: IProfessionalProfile = {
		timezone: 'America/Sao_Paulo',
		canAcceptTags: [],
		marginInMinutesToSchedule: 60,
		marginInMinutesToReschedule: 60,
	} as IProfessionalProfile;

	constructor(
		public user: UserService,
		public translate: TranslateService,
		public notifications: ZkNotificationService,
		protected zkuService: ZkuService,
		private actionTrackerService: ActionTrackerService
	) {
		this.emitChanges();

		const userService = this.zkuService.getZku();
		if (
			userService !== undefined &&
			userService?.tags?.some((tag) => tag['id'] === 'professional_zen')
		) {
			this.isProfessionalZen = true;
		}
	}

	async ngOnInit() {
		const data = await this.user.getProfileData();

		this.data = {
			...this.data,
			...data,
			timezone: data.timezone || '',
		};

		this.setMarginOptions();
		this.setTimezoneOptions();
		await this.checkSubscriptionAndContactTiers();
		await this.checkSessionPriceUpdate();
		this.getSummaryCorporate();

		this.tagsIds = this.data.tags.map((tag) => tag.id);
		this.isLoading = false;

		this.checkIfSelectedTiersAcceptsHalfHour();
	}

	updateData(key: string, value: any) {
		this.formChanged = true;
		this.data[key] = value;
	}

	checkSubscriptionAndContactTiers(): Promise<void> {
		return new Promise((resolve) => {
			this.user
				.checkSubscriptionAndContactTiers()
				.then(({ hasActiveSubscription, contactTiers }) => {
					this.isPremium = hasActiveSubscription;
					this.contactTiers = contactTiers;
					resolve();
				});
		});
	}

	checkSessionPriceUpdate(): Promise<void> {
		return new Promise((resolve) => {
			this.user
				.checkSessionPriceUpdate()
				.then(({ canUpdateOneHour, canUpdateHalfHour }) => {
					this.canUpdateOneHour = canUpdateOneHour;
					this.canUpdateHalfHour = canUpdateHalfHour;
					resolve();
				});
		});
	}

	hasContactForTier(tier: string): boolean {
		return (
			this.contactTiers.includes(tier) &&
			!this.data.canAcceptTags.includes(tier)
		);
	}

	setTimezoneOptions(): void {
		const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		if (browserTimezone) {
			Object.entries(timezones).forEach(([key]) => {
				if (browserTimezone === key && !this.data.timezone) {
					this.updateData('timezone', key);
				}
			});
		}

		const filterParentheses = (val = ''): string =>
			val.replace('(', '').replace(')', '');

		this.timezoneOptions = Object.entries(timezones).map(([key, value]) => ({
			value: key,
			label: filterParentheses(value as string),
		}));
	}

	setMarginOptions(): void {
		const strHour: string = this.translate.instant('GENERAL.HOUR');
		const strHours: string = this.translate.instant('GENERAL.HOURS');

		const minToHour = (min) => min / 60;
		const margins = [60, 120, 180, 360, 720, 1440];

		this.marginOptions = margins.map((value) => ({
			value: String(value),
			label:
				minToHour(value) + ' ' + (minToHour(value) > 1 ? strHours : strHour),
		}));
	}

	toggleTier(tierId: string): void {
		const { canAcceptTags } = this.data;

		if (canAcceptTags.includes(tierId)) {
			this.updateData(
				'canAcceptTags',
				canAcceptTags.filter((tag) => tag !== tierId)
			);
		} else {
			this.updateData('canAcceptTags', [...canAcceptTags, tierId]);
		}
		this.formChanged = true;
		this.checkIfSelectedTiersAcceptsHalfHour();
		this.emitChanges();
	}

	checkIfSelectedTiersAcceptsHalfHour(): void {
		const selectedTierThatAcceptsHalfHour = this.tiers.some(
			(tier) =>
				this.data.canAcceptTags.includes(tier.id) && tier.acceptsHalfHour
		);

		this.selectedTierThatAcceptsHalfHour = selectedTierThatAcceptsHalfHour;
		this.data.acceptsHalfHour = selectedTierThatAcceptsHalfHour
			? this.data.acceptsHalfHour
			: false;
	}

	tierIsSelected = (tierId: string): boolean => {
		return this.data.canAcceptTags.includes(tierId);
	};

	getTierTooltipMessage(): string {
		if (!this.isPremium) {
			return this.translate.instant(
				'MY_ACCOUNT.PLAN_SESSION.FORM.TIER.MUST_BE_PREMIUM_TOOLTIP'
			);
		}
	}

	toString = (val): string => String(val);

	onChangeForm(field, value): void {
		switch (field) {
			case 'marginInMinutesToSchedule':
				this.updateData(
					field,
					this.marginOptions.find((elm) => elm.value === value)?.value
				);
				this.updateData(
					field,
					this.data[field] ? Number(this.data[field]) : this.data[field]
				);
				break;

			case 'marginInMinutesToReschedule':
				this.updateData(
					field,
					this.marginOptions.find((elm) => elm.value === value)?.value
				);
				this.updateData(
					field,
					this.data[field] ? Number(this.data[field]) : this.data[field]
				);
				break;

			case 'acceptsHalfHour':
				this.updateData(field, value);
				break;

			default:
				this.updateData(field, value);
				break;
		}

		this.formChanged = true;
		this.emitChanges();
	}

	saveForm(): void {
		if (!this.formChanged) {
			return;
		}
		if (this.isProfessionalZen && !this.data.canAcceptTags.length) {
			this.notifications.showError(
				this.translate.instant(
					'MY_ACCOUNT.PLAN_SESSION.FORM.TIER.SHOW_ERROR_EMPTY_TIERS'
				)
			);
			return;
		}
		if (!this.data.priceOneHour) {
			this.notifications.showError(
				this.translate.instant(
					'MY_ACCOUNT.PLAN_SESSION.ERRORS.REQUIRED_PRICE',
					{
						value: 50,
					}
				)
			);

			return;
		} else if (this.data.acceptsHalfHour && !this.data.priceHalfHour) {
			this.notifications.showError(
				this.translate.instant(
					'MY_ACCOUNT.PLAN_SESSION.ERRORS.REQUIRED_PRICE',
					{
						value: 30,
					}
				)
			);

			return;
		}

		if (!this.isSaving) {
			this.isSaving = true;

			const data = {
				acceptsHalfHour: this.data.acceptsHalfHour || false,
				timezone: this.data.timezone,
				marginInMinutesToSchedule: this.data.marginInMinutesToSchedule,
				marginInMinutesToReschedule: this.data.marginInMinutesToReschedule,
				canAcceptTags: this.data.canAcceptTags || [],
				blockNewClients: this.isProfessionalZen
					? true
					: this.data.blockNewClients,
				acceptOnlyWholeHour: this.data.acceptOnlyWholeHour,
			};

			this.user.updateProfileData(data, '/plan').subscribe(
				() => {
					this.actionTrackerService.eventTracker(
						SCOPE_TYPE.Account,
						SUBSCOPE_TYPE.Plan,
						ITEM_TYPE.Definitions,
						INTERACTION_TYPE.Submit,
						data
					);
					this.isSaving = false;
					this.updateLocalData(this.data);
					this.userUpdated.emit();
					this.notifications.showSuccess(
						this.translate.instant('MESSAGES.SAVEDSUCCESSFULLY')
					);
					this.formChanged = false;
					this.emitChanges();
				},
				(error: ApiErrorResponse) => {
					this.isSaving = false;
					this.notifications.showError(
						(error?.error as ApiError)?.messageI18n,
						this.translate.instant(
							'MY_ACCOUNT.PLAN_SESSION.ERRORS.GENERIC_TITLE'
						)
					);
				}
			);
		}
	}

	updateLocalData(data: IProfessionalProfile): void {
		this.user
			.getProfessionalProfileData(this.data.id)
			.subscribe((user: IProfessionalProfile) => {
				if (user) {
					this.user.saveData({
						...user,
						priceOneHour: data.priceOneHour,
						priceHalfHour: data.priceHalfHour,
					});
				}
			});
	}

	openModal(step: ModalSteps): void {
		this.modalData.priceHalfHour = this.data.priceHalfHour || 0;
		this.modalData.priceOneHour = this.data.priceOneHour || 0;

		this.modalStep = step;
		this.isOpenModal = true;
		this.modalError = '';
	}

	getModalPrice(): number {
		const step = this.modalStep;

		if (
			step === ModalSteps['update50MinPrice'] ||
			step === ModalSteps['confirm50MinPrice']
		) {
			return this.modalData.priceOneHour;
		} else {
			return this.modalData.priceHalfHour;
		}
	}

	isInteger(n: number): boolean {
		return n % 1 === 0;
	}

	setModalError(step: ModalSteps, price: number): void {
		if (!this.isInteger(price)) {
			this.modalError = this.translate.instant(
				'MY_ACCOUNT.PLAN_SESSION.ERRORS.NON_INTEGER_PRICE'
			);
		} else if (step === ModalSteps['update30MinPrice'] && price < 1) {
			this.modalError = this.translate.instant(
				'MY_ACCOUNT.PLAN_SESSION.ERRORS.MIN_PRICE',
				{
					value: 1,
				}
			);
		} else if (step === ModalSteps['update50MinPrice'] && price < 60) {
			this.modalError = this.translate.instant(
				'MY_ACCOUNT.PLAN_SESSION.ERRORS.MIN_PRICE',
				{
					value: 60,
				}
			);
		} else {
			this.modalError = '';
		}
	}

	setModalPrice(price: number): void {
		const step = this.modalStep;

		if (step === ModalSteps['update50MinPrice']) {
			this.modalData.priceOneHour = price;
		} else {
			this.modalData.priceHalfHour = price;
		}

		this.setModalError(step, price);
	}

	leftButtonModal(): void {
		switch (this.modalStep) {
			case ModalSteps['update50MinPrice']:
				this.isOpenModal = false;
				break;
			case ModalSteps['update30MinPrice']:
				this.isOpenModal = false;
				break;
			case ModalSteps['confirm50MinPrice']:
				this.modalStep = ModalSteps['update50MinPrice'];
				break;
			case ModalSteps['confirm30MinPrice']:
				this.modalStep = ModalSteps['update30MinPrice'];
				break;
		}
	}

	rightButtonModal(): void {
		const price =
			this.modalStep === ModalSteps['update50MinPrice']
				? this.modalData.priceOneHour
				: this.modalData.priceHalfHour;
		this.setModalError(this.modalStep, price);
		if (this.modalError) return;

		switch (this.modalStep) {
			case ModalSteps['update50MinPrice']:
				this.modalStep = ModalSteps['confirm50MinPrice'];
				break;
			case ModalSteps['update30MinPrice']:
				this.modalStep = ModalSteps['confirm30MinPrice'];
				break;
			case ModalSteps['confirm50MinPrice']:
				this.updateSessionPrice({
					priceOneHour: this.modalData.priceOneHour,
				});
				break;
			case ModalSteps['confirm30MinPrice']:
				this.updateSessionPrice({
					priceHalfHour: this.modalData.priceHalfHour,
				});
				break;
		}
	}

	translateIt(key: string): string {
		return this.translate.instant(key);
	}

	updateSessionPrice(data: UpdateSessionPrice): void {
		this.isSavingModal = true;
		this.user.updateProfileData(data, '/sessionPrice').subscribe(
			async () => {
				this.userUpdated.emit();

				if (data.priceOneHour) {
					this.updateData('priceOneHour', data.priceOneHour);
				}

				if (data.priceHalfHour) {
					this.updateData('priceHalfHour', data.priceHalfHour);
				}

				await this.checkSessionPriceUpdate();

				this.isSavingModal = false;
				this.isOpenModal = false;

				this.updateLocalData(this.data);
			},
			(error: ApiErrorResponse) => {
				this.isSavingModal = false;
				this.isOpenModal = false;
				this.notifications.showError(
					(error?.error as ApiError)?.messageI18n,
					this.translate.instant('MY_ACCOUNT.PLAN_SESSION.ERRORS.GENERIC_TITLE')
				);
			}
		);
	}

	emitChanges(): void {
		this.unsavedChanges.emit(this.formChanged);
	}

	getSummaryCorporate(): void {
		this.user.getSummaryCorporate().subscribe((tiers) => {
			for (const tier of tiers) {
				const tierIndex = this.tiers.findIndex(
					(_tier) => _tier.id === tier.tier
				);

				if (tierIndex < 0) continue;

				if (tier.duration === 60) {
					this.tiers[tierIndex].elegible.full = tier.totalEligibles;
				}

				if (tier.duration === 30) {
					this.tiers[tierIndex].elegible.half = tier.totalEligibles;
				}
			}
		});
	}
}
